import {
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { Router, RouterOutlet, TitleStrategy } from '@angular/router';
import { MainNavigationComponent } from './shared/components/main-navigation/main-navigation.component';
import { MainHeaderComponent } from './shared/components/main-header/main-header.component';
import { MainFooterComponent } from './shared/components/main-footer/main-footer.component';
import { WsConnectorService } from './core/services/ws-connector.service';
import { WsDemoConnectorService } from './core/services/ws-demo-connector.service';
import { AsyncPipe } from '@angular/common';
import { LocalAuthService, UserService } from '@sso/ld-sso-with-oauth2-oidc';
import { hasRmDemoRole } from './core/enums/role.enum';
import { SystemStopComponent } from './features/dashboard/components/system-stop/system-stop.component';
import { Store } from '@ngrx/store';
import { KillSwitchActions } from './features/dashboard/state/kill-switch.actions';
import {
  selectIsEmergencyStopActive,
  selectIsSystemRunning,
} from './features/dashboard/state/kill-switch.selectors';
import { animate, style, transition, trigger } from '@angular/animations';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import {
  EMPTY,
  catchError,
  distinctUntilChanged,
  map,
  of,
  switchMap,
} from 'rxjs';
import { NotificationActions } from './features/notifications/state/notification.actions';
import { IdleTimeoutService } from './core/services/idle-timeout.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    MainNavigationComponent,
    MainHeaderComponent,
    //MainFooterComponent,
    AsyncPipe,
    SystemStopComponent,
  ],
  providers: [],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  animations: [
    trigger('fadeSlide', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate(
          '300ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
      transition(':leave', [
        animate(
          '300ms ease-in',
          style({ opacity: 0, transform: 'translateY(-10px)' }),
        ),
      ]),
    ]),
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private _store = inject(Store);
  private _wsConnectorService = inject(WsConnectorService);
  private _wsDemoConnectorService = inject(WsDemoConnectorService);
  private _localAuthService = inject(LocalAuthService);
  private _userService = inject(UserService);
  // Injected here only to eagerly start its idle-watch logic on app boot.
  private _idleTimeoutService = inject(IdleTimeoutService);
  private router = inject(Router);
  private titleStrategy = inject(TitleStrategy);
  private translateService = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  isAuthenticated$ = this._localAuthService.isAuthenticated$;
  isSystemRunning$ = this._store.select(selectIsSystemRunning);
  isEmegancyStopActive$ = this._store.select(selectIsEmergencyStopActive);

  ngOnInit() {
    this.isAuthenticated$
      .pipe(
        distinctUntilChanged(),
        switchMap((isAuthenticated) => {
          if (!isAuthenticated) {
            this._wsConnectorService.close();
            this._wsDemoConnectorService.close();
            return EMPTY;
          }

          this._store.dispatch(KillSwitchActions.public.loadKillSwitch());
          this._store.dispatch(KillSwitchActions.public.loadEmergencyStop());
          this._store.dispatch(
            NotificationActions.public.loadLatestNotifications(),
          );

          // Showcase (RM_DEMO/RM_DEMO_GROUP) users get the /ws/v2/demo feed
          // instead of the notifications/devices one - the two connections
          // are mutually exclusive (see subscribe below). Resolved via a
          // fresh getUserInfo$() call rather than IdleTimeoutService's
          // shared isShowcase$: that stream is a shareReplay(1) spanning
          // both the logged-out and logged-in state, so a subscriber
          // joining here (right as isAuthenticated flips to true) gets
          // whatever was buffered from *before* login - always "not a demo
          // user" - instead of waiting for the real role. That stale value
          // was enough to briefly open a real /ws/v2/notifications
          // connection for showcase users, which then had to be torn down
          // again once the real role resolved. A call scoped to this
          // authenticated session only ever resolves to the real role
          // (UserService caches internally, so this doesn't add a real
          // extra request once any other consumer has already resolved it).
          return this._userService.getUserInfo$().pipe(
            map((user) => hasRmDemoRole(user?.info?.roles ?? [])),
            catchError(() => of(false)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((isShowcase) => {
        if (isShowcase) {
          this._wsConnectorService.close();
          this._wsDemoConnectorService.initSocket().then();
        } else {
          this._wsDemoConnectorService.close();
          this._wsConnectorService.initSocket().then();
        }
      });

    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.titleStrategy.updateTitle(this.router.routerState.snapshot);
      });
  }

  ngOnDestroy() {
    this._wsConnectorService.close();
    this._wsDemoConnectorService.close();
  }

  onTriggerKillSwitch(value: boolean) {
    this._store.dispatch(
      KillSwitchActions.public.triggerKillSwitch({ value: value }),
    );
  }
}
